import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { updateMyProfile, exportPersonalData, deactivateAccount } from '../api/users';
import { useAuthStore } from '../store/authStore';
import { useSignOut } from '../hooks/useSignOut';
import { BottomNav } from '../components/ui/BottomNav';
import { LanguagePicker } from '../components/ui/LanguagePicker';
import NotificationPreferencesPanel from '../components/NotificationPreferencesPanel';
import LocationAutomationPanel from '../components/LocationAutomationPanel';
import TelegramLinkPanel from '../components/TelegramLinkPanel';
import { isNativePlatform } from '../lib/runtime';
import { useTranslation } from '../i18n';
import analytics from '../lib/analytics';

// ─── constants ────────────────────────────────────────────────────────────────

// ─── ProfilePage ──────────────────────────────────────────────────────────────

export default function ProfilePage() {
  const navigate = useNavigate();
  const user    = useAuthStore((s) => s.user);
  const signOut = useSignOut();
  const { t, direction } = useTranslation();

  // ── notifications state ───────────────────────────────────────────────────
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [phoneInput,   setPhoneInput]   = useState(user?.phone ?? '');
  const [nameInput,    setNameInput]    = useState(user?.name ?? '');
  const [notifToast,   setNotifToast]   = useState<string | null>(null);
  const [notifError,   setNotifError]   = useState<string | null>(null);

  // ── save name ─────────────────────────────────────────────────────────────

  const saveNameMutation = useMutation({
    mutationFn: (name: string) => updateMyProfile({ name }),
    onSuccess: (res) => {
      const updated = res.data.data as { name: string };
      useAuthStore.setState((s) => ({
        user: s.user ? { ...s.user, name: updated.name } : null,
      }));
      analytics.trackProfileUpdated('name');
      setNotifError(null);
      setNotifToast(t('settings_saved'));
      setTimeout(() => setNotifToast(null), 2000);
    },
    onError: () => setNotifError(t('failed_save')),
  });

  // ── save phone number ─────────────────────────────────────────────────────

  const savePhoneMutation = useMutation({
    mutationFn: (phone: string | null) => updateMyProfile({ phone }),
    onSuccess: (res) => {
      const updated = res.data.data as { phone: string | null; whatsapp_opt_in: boolean };
      useAuthStore.setState((s) => ({
        user: s.user ? { ...s.user, phone: updated.phone, whatsapp_opt_in: updated.whatsapp_opt_in } : null,
      }));
      analytics.trackProfileUpdated('phone');
      setNotifError(null);
      setNotifToast(t('settings_saved'));
      setTimeout(() => setNotifToast(null), 2000);
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
      if (code === 'PHONE_ALREADY_EXISTS') {
        setNotifError(t('phone_already_exists'));
      } else {
        setNotifError(t('failed_save'));
      }
    },
  });

  // ── toggle whatsapp opt-in ────────────────────────────────────────────────

  const toggleOptInMutation = useMutation({
    mutationFn: (value: boolean) => updateMyProfile({ whatsapp_opt_in: value }),
    onSuccess: (res) => {
      const updated = res.data.data as { phone: string | null; whatsapp_opt_in: boolean };
      useAuthStore.setState((s) => ({
        user: s.user ? { ...s.user, whatsapp_opt_in: updated.whatsapp_opt_in } : null,
      }));
      analytics.trackProfileUpdated('whatsapp_opt_in');
      setNotifError(null);
      setNotifToast(t('settings_saved'));
      setTimeout(() => setNotifToast(null), 2000);
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
      if (code === 'VALIDATION_ERROR') {
        setNotifError(t('add_whatsapp_before'));
      } else {
        setNotifError(t('failed_save'));
      }
    },
  });

  // Push-notification master + sub-toggles are owned by
  // NotificationPreferencesPanel (which itself wires usePushNotificationToggle
  // for browser permission + FCM token registration). Nothing to do here.

  // ── GDPR export and deactivation ──────────────────────────────────────────

  const exportMutation = useMutation({
    mutationFn: () => exportPersonalData(),
    onSuccess: (res) => {
      const blob = res.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'personal_data.json');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      analytics.trackGdprExportRequested();
      setNotifToast(t('export_downloaded'));
      setTimeout(() => setNotifToast(null), 2000);
    },
    onError: () => {
      setNotifError(t('export_failed'));
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: () => deactivateAccount(),
    onSuccess: () => {
      analytics.trackAccountSelfDeactivated();
      setShowDeactivateConfirm(false);
      void signOut();
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: { code?: string; message?: string } } } })?.response?.data?.error?.code;
      const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      if (code === 'USER_HAS_ACTIVE_SESSION') {
        setNotifError(t('cannot_deactivate_active_session'));
      } else {
        setNotifError(message || t('deactivation_failed'));
      }
      setShowDeactivateConfirm(false);
    },
  });

  if (!user) return null;

  const initials = user.name.charAt(0).toUpperCase();

  return (
    <div className="page" style={{ maxWidth: '430px', margin: '0 auto' }}>
      {/* Top bar */}
      <header className="topbar" style={{
        position:       'sticky',
        top:            0,
        zIndex:         50,
        height:         '64px',
        padding:        '0 24px',
        display:        'flex',
        justifyContent: 'space-between',
        alignItems:     'center',
        boxShadow:      '0 12px 32px -4px rgba(43,52,55,0.06)',
      }}>
        <div style={{ width: '84px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <LanguagePicker />
        </div>
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 114 26" style={{ height: '26px', width: 'auto', display: 'block' }} aria-label="Lot'N'Go">
            <text x="57" y="20" fontFamily="Manrope, system-ui, sans-serif" fontWeight="700" fontSize="20" fill="#005bc0" textAnchor="middle" letterSpacing="-0.4">{"Lot'N'Go"}</text>
          </svg>
        </div>
        <button
          onClick={() => setShowSignOutConfirm(true)}
          style={{
            background:  'none',
            border:      'none',
            cursor:      'pointer',
            color:       'var(--on-surface-3)',
            padding:     '8px',
            display:     'flex',
            alignItems:  'center',
          }}
          title={t('sign_out')}
          aria-label={t('sign_out')}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>logout</span>
        </button>
      </header>

      <div className="page-body">

        {/* 1. User info card */}
        <div style={{ ...card, marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={avatar}>{initials}</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '16px', marginBottom: '2px' }}>
                {user.name}
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>{user.email}</div>
              <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                {t('apt', { apartment: user.apartment })} · {user.building.name}
              </div>
            </div>
          </div>
        </div>

        {/* 2. Account section */}
        <div style={sectionLabel}>{t('account')}</div>
        <div style={{ ...card, marginBottom: '24px', padding: 0, overflow: 'hidden' }}>
          <button
            onClick={() => void signOut()}
            style={row}
          >
            <span style={{ fontSize: '14px', color: '#111' }}>{t('sign_out')}</span>
            <span style={{ color: '#9ca3af', fontSize: '18px', lineHeight: 1 }}>›</span>
          </button>
        </div>

        {/* 3. Personal Details section */}
        <div style={sectionLabel}>{t('personal_details')}</div>
        <div style={{ ...card, marginBottom: '24px' }}>

          {/* Toast / error */}
          {notifToast && (
            <div style={{ fontSize: '12px', color: '#16a34a', marginBottom: '12px', fontWeight: 600 }}>
              ✓ {notifToast}
            </div>
          )}
          {notifError && (
            <div style={{ fontSize: '12px', color: '#dc2626', marginBottom: '12px' }}>
              {notifError}
            </div>
          )}

          {/* Name input + save. Save button is only rendered when the field
              is dirty AND non-empty — name is mandatory, so clearing the
              field hides the save instead of offering to persist an empty
              value the API would reject. */}
          <div style={{ marginBottom: '16px' }}>
            <label style={fieldLabel}>{t('name')}</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={nameInput}
                onChange={(e) => { setNameInput(e.target.value); setNotifError(null); }}
                placeholder={t('name')}
                style={inputStyle}
              />
              {nameInput.trim() !== '' && nameInput.trim() !== (user.name ?? '') && (
                <button
                  onClick={() => saveNameMutation.mutate(nameInput.trim())}
                  disabled={saveNameMutation.isPending}
                  style={saveBtn}
                >
                  {saveNameMutation.isPending ? '…' : t('save')}
                </button>
              )}
            </div>
          </div>

          {/* Phone input + save. Phone is treated as mandatory here — same
              clearing-hides-save semantics as name. */}
          <div style={{ marginBottom: '16px' }}>
            <label style={fieldLabel}>{t('whatsapp_number')}</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="tel"
                value={phoneInput}
                onChange={(e) => { setPhoneInput(e.target.value); setNotifError(null); }}
                placeholder="+972 50 123 4567"
                style={inputStyle}
              />
              {phoneInput.trim() !== '' && phoneInput.trim() !== (user.phone ?? '') && (
                <button
                  onClick={() => savePhoneMutation.mutate(phoneInput.trim())}
                  disabled={savePhoneMutation.isPending}
                  style={saveBtn}
                >
                  {savePhoneMutation.isPending ? '…' : t('save')}
                </button>
              )}
            </div>
          </div>

          {/* Opt-in toggle */}
          <div style={{ marginBottom: '20px', display: 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, marginRight: '12px' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#111', marginBottom: '2px' }}>
                  {t('receive_whatsapp_alerts')}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  {user.phone
                    ? t('whatsapp_notification_desc')
                    : t('add_number_first')}
                </div>
              </div>
              <button
                role="switch"
                aria-checked={user.whatsapp_opt_in}
                disabled={!user.phone || toggleOptInMutation.isPending}
                onClick={() => !user.phone
                  ? setNotifError('Add a WhatsApp number before enabling notifications')
                  : toggleOptInMutation.mutate(!user.whatsapp_opt_in)
                }
                title={!user.phone ? 'Add a number first' : undefined}
                style={{
                  ...toggle,
                  backgroundColor: user.whatsapp_opt_in ? '#16a34a' : '#d1d5db',
                  opacity: !user.phone ? 0.4 : 1,
                  cursor: !user.phone ? 'not-allowed' : 'pointer',
                }}
              >
                <span style={{
                  ...toggleThumb,
                  transform: user.whatsapp_opt_in
                    ? direction === 'rtl'
                      ? 'translateX(-20px)'
                      : 'translateX(20px)'
                    : 'translateX(2px)',
                }} />
              </button>
            </div>
          </div>

          {/* Push notification master + sub-toggles have moved into the
              dedicated NotificationPreferencesPanel below. */}
        </div>

        {/* 3b. Notification Topics — master + per-topic toggles */}
        <NotificationPreferencesPanel />

        {/* 3c. Smart Auto-Release — only meaningful inside the Capacitor shell.
            The native plugin is what actually evaluates location + ends the
            session; on the PWA the panel would just call no-op tombstones and
            mislead the user into thinking the feature is active. Hide it. */}
        {import.meta.env.VITE_FEATURE_LOCATION_AUTOMATION === 'true' && isNativePlatform() && (
          <LocationAutomationPanel />
        )}

        {/* 3d. Connect Telegram */}
        <TelegramLinkPanel />

        {/* 4. Help & Support section */}
        <div style={sectionLabel}>{t('help_support')}</div>
        <div style={{ ...card, marginBottom: '24px', padding: 0, overflow: 'hidden' }}>
          <a
            href="https://youtube.com/shorts/apAekxLcbRA"
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...row, textDecoration: 'none' }}
          >
            <span style={{ fontSize: '14px', color: '#111' }}>{t('watch_app_tour')}</span>
            <span style={{ color: '#9ca3af', fontSize: '18px', lineHeight: 1 }}>›</span>
          </a>
          <div style={{ height: '1px', backgroundColor: 'rgba(43,52,55,0.06)' }} />
          <button
            onClick={() => navigate('/feedback')}
            style={row}
          >
            <span style={{ fontSize: '14px', color: '#111' }}>{t('send_feedback')}</span>
            <span style={{ color: '#9ca3af', fontSize: '18px', lineHeight: 1 }}>›</span>
          </button>
        </div>

        {/* 5. GDPR Privacy section */}
        <div style={sectionLabel}>{t('privacy_security')}</div>
        <div style={{ ...card, marginBottom: '24px', padding: 0, overflow: 'hidden' }}>
          <button
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
            style={{
              ...row,
              opacity: exportMutation.isPending ? 0.6 : 1,
              cursor: exportMutation.isPending ? 'not-allowed' : 'pointer',
            }}
          >
            <span style={{ fontSize: '14px', color: '#111' }}>
              {exportMutation.isPending ? t('exporting') : t('download_personal_data')}
            </span>
            <span style={{ color: '#9ca3af', fontSize: '18px', lineHeight: 1 }}>›</span>
          </button>
          <div style={{ height: '1px', backgroundColor: 'rgba(43,52,55,0.06)' }} />
          <button
            onClick={() => setShowDeactivateConfirm(true)}
            style={{
              ...row,
              color: '#dc2626',
            }}
          >
            <span style={{ fontSize: '14px', color: '#dc2626' }}>{t('deactivate_account')}</span>
            <span style={{ color: '#fca5a5', fontSize: '18px', lineHeight: 1 }}>›</span>
          </button>
        </div>

        {/* 6. Building section (all users) */}
        <div style={sectionLabel}>{t('building')}</div>
        <div style={{ ...card, marginBottom: '24px', padding: 0, overflow: 'hidden' }}>
          <div style={infoRow}>
            <span style={infoLabel}>{t('name')}</span>
            <span style={infoValue}>{user.building.name}</span>
          </div>
          <div style={infoRow}>
            <span style={infoLabel}>{t('timezone')}</span>
            <span style={infoValue}>{user.building.timezone}</span>
          </div>
        </div>

        {/* 7. Version label */}
        <div style={{
          textAlign: 'center',
          fontSize: '12px',
          color: '#9ca3af',
          marginBottom: '24px',
          opacity: 0.6
        }}>
          v{typeof window !== 'undefined' && sessionStorage.getItem('app_version') ? sessionStorage.getItem('app_version') : '1.0.0'}
        </div>

      </div>

      <BottomNav />

      {/* Sign out confirm */}
      {showSignOutConfirm && (
        <>
          <div
            onClick={() => setShowSignOutConfirm(false)}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 300 }}
          />
          <div style={{
            position: 'fixed', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'calc(100% - 48px)', maxWidth: '320px',
            backgroundColor: '#ffffff', borderRadius: '20px',
            padding: '24px 20px 16px', zIndex: 301,
            boxShadow: '0 12px 32px -4px rgba(43,52,55,0.12)',
          }}>
            <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '6px' }}>
              {t('sign_out')} of Lot'N'Go?
            </div>
            <div className="body-md" style={{ marginBottom: '20px' }}>
              {t('sign_out_desc')}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setShowSignOutConfirm(false)} className="btn-secondary" style={{ flex: 1, height: '44px' }}>
                {t('cancel')}
              </button>
              <button onClick={() => { setShowSignOutConfirm(false); void signOut(); }} className="btn-primary" style={{ flex: 1, height: '44px' }}>
                {t('sign_out')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Deactivate account confirm */}
      {showDeactivateConfirm && (
        <>
          <div
            onClick={() => !deactivateMutation.isPending && setShowDeactivateConfirm(false)}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 300 }}
          />
          <div style={{
            position: 'fixed', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'calc(100% - 48px)', maxWidth: '320px',
            backgroundColor: '#ffffff', borderRadius: '20px',
            padding: '24px 20px 16px', zIndex: 301,
            boxShadow: '0 12px 32px -4px rgba(43,52,55,0.12)',
          }}>
            <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '6px', color: '#dc2626' }}>
              {t('deactivate_account')}
            </div>
            <div className="body-md" style={{ marginBottom: '20px' }}>
              {t('deactivate_confirm_desc')}
            </div>
            {notifError && (
              <div style={{ fontSize: '12px', color: '#dc2626', marginBottom: '12px' }}>
                {notifError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setShowDeactivateConfirm(false)}
                className="btn-secondary"
                style={{ flex: 1, height: '44px' }}
                disabled={deactivateMutation.isPending}
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => deactivateMutation.mutate()}
                className="btn-primary"
                style={{ flex: 1, height: '44px', backgroundColor: '#dc2626' }}
                disabled={deactivateMutation.isPending}
              >
                {deactivateMutation.isPending ? t('deactivating') : t('deactivate')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── styles ───────────────────────────────────────────────────────────────────

const card: React.CSSProperties = {
  backgroundColor: '#ffffff',
  borderRadius:    '14px',
  boxShadow:       '0 12px 32px -4px rgba(43,52,55,0.06)',
  padding:         '14px 16px',
};

const sectionLabel: React.CSSProperties = {
  fontSize:      '12px',
  fontWeight:    600,
  color:         '#9ca3af',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom:  '6px',
};

const avatar: React.CSSProperties = {
  width:        '48px',
  height:       '48px',
  borderRadius: '50%',
  background:   'linear-gradient(135deg, #005bc0, #004fa9)',
  color:        '#f7f7ff',
  display:      'flex',
  alignItems:   'center',
  justifyContent:'center',
  fontSize:     '20px',
  fontWeight:   700,
  flexShrink:   0,
};

const row: React.CSSProperties = {
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'space-between',
  width:          '100%',
  padding:        '13px 16px',
  background:     'none',
  border:         'none',
  cursor:         'pointer',
  textAlign:      'start',
};

const infoRow: React.CSSProperties = {
  display:        'flex',
  justifyContent: 'space-between',
  alignItems:     'center',
  padding:        '12px 16px',
};

const infoLabel: React.CSSProperties = {
  fontSize: '14px',
  color:    '#374151',
};

const infoValue: React.CSSProperties = {
  fontSize: '14px',
  color:    '#9ca3af',
};

const fieldLabel: React.CSSProperties = {
  display:      'block',
  fontSize:     '12px',
  fontWeight:   600,
  color:        '#374151',
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  flex:            1,
  height:          '38px',
  borderRadius:    '8px',
  border:          '1px solid rgba(43,52,55,0.12)',
  padding:         '0 10px',
  fontSize:        '14px',
  backgroundColor: '#ffffff',
  minWidth:        0,
};

const saveBtn: React.CSSProperties = {
  height:       '38px',
  padding:      '0 14px',
  borderRadius: '8px',
  border:       'none',
  background:   'linear-gradient(135deg, #005bc0, #004fa9)',
  color:        '#f7f7ff',
  fontSize:     '13px',
  fontWeight:   600,
  cursor:       'pointer',
  flexShrink:   0,
};

const toggle: React.CSSProperties = {
  position:   'relative',
  display:    'inline-flex',
  alignItems: 'center',
  width:      '44px',
  height:     '26px',
  borderRadius:'13px',
  border:     'none',
  flexShrink: 0,
  transition: 'background-color 0.2s',
  padding:    0,
};

const toggleThumb: React.CSSProperties = {
  position:        'absolute',
  width:           '22px',
  height:          '22px',
  borderRadius:    '50%',
  backgroundColor: '#fff',
  boxShadow:       '0 1px 3px rgba(0,0,0,0.3)',
  transition:      'transform 0.2s',
};
